const SVG_NS = 'http://www.w3.org/2000/svg';

const LOCK_SLACK_PX = 6;

const lockBox = (root: HTMLElement, width: number, height: number) => {
  const safeHeight = Math.ceil(height) + LOCK_SLACK_PX;
  root.style.boxSizing = 'border-box';
  root.style.width = `${Math.round(width)}px`;
  root.style.maxWidth = `${Math.round(width)}px`;
  root.style.height = `${safeHeight}px`;
  root.style.maxHeight = `${safeHeight}px`;
  root.style.minHeight = '0';
  root.style.overflow = 'visible';
  root.style.background = '#ffffff';
};

const forceChartStack = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('[data-pdf-charts-grid]').forEach((grid) => {
    grid.style.setProperty('display', 'block', 'important');
    grid.style.setProperty('width', '100%', 'important');
    grid.style.setProperty('gap', '0', 'important');
    grid.style.setProperty('grid-template-columns', 'none', 'important');
  });

  root.querySelectorAll<HTMLElement>('[data-pdf-chart-column]').forEach((column, index, columns) => {
    column.style.setProperty('display', 'block', 'important');
    column.style.setProperty('width', '100%', 'important');
    column.style.setProperty('max-width', '100%', 'important');
    column.style.setProperty('min-width', '0', 'important');
    column.style.setProperty('float', 'none', 'important');
    column.style.setProperty('position', 'relative', 'important');
    column.style.setProperty('overflow', 'hidden', 'important');
    column.style.setProperty('margin', index === columns.length - 1 ? '0' : '0 0 20px 0', 'important');
  });
};

const fitChartSvgs = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('[data-pdf-chart-wrap]').forEach((wrap) => {
    const svg = wrap.querySelector('svg');
    if (!svg) return;

    const svgWidth = Number(svg.getAttribute('width'));
    const svgHeight = Number(svg.getAttribute('height'));

    wrap.style.setProperty('display', 'block', 'important');
    wrap.style.setProperty('width', '100%', 'important');
    wrap.style.setProperty('position', 'relative', 'important');
    wrap.style.setProperty('overflow', 'hidden', 'important');
    if (svgHeight > 0) wrap.style.setProperty('height', `${svgHeight}px`, 'important');

    const width = Math.round(wrap.clientWidth);
    const height = Math.round(wrap.clientHeight);
    if (!svgWidth || !svgHeight || !width || !height) return;

    if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
    svg.style.display = 'block';
    svg.style.overflow = 'hidden';

    const host = svg.parentElement;
    if (host instanceof HTMLElement) {
      host.style.position = 'relative';
      host.style.width = `${width}px`;
      host.style.height = `${height}px`;
      host.style.overflow = 'hidden';
    }
  });
};

const flattenSvgText = (root: HTMLElement) => {
  root.querySelectorAll('svg text').forEach((text) => {
    const tspans = Array.from(text.children).filter((child) => child.tagName.toLowerCase() === 'tspan');
    if (tspans.length !== 1) return;
    text.textContent = tspans[0].textContent ?? '';
  });
};

const cullOverlappingXTicks = (root: HTMLElement) => {
  root.querySelectorAll('.recharts-xAxis').forEach((axis) => {
    const ticks = Array.from(axis.querySelectorAll<SVGGElement>('.recharts-cartesian-axis-tick'));
    let lastRight = Number.NEGATIVE_INFINITY;
    let lastShown: SVGGElement | null = null;

    ticks.forEach((tick, index) => {
      const text = tick.querySelector('text');
      if (!text) return;

      let box: DOMRect;
      try {
        box = text.getBBox();
      } catch {
        return;
      }
      if (box.width <= 0) return;

      const isLast = index === ticks.length - 1;
      const overlaps = box.x < lastRight + 8;
      if (index > 0 && overlaps && !isLast) {
        tick.style.display = 'none';
        return;
      }
      if (isLast && overlaps && lastShown && index > 1) {
        lastShown.style.display = 'none';
      }
      lastRight = box.x + box.width;
      lastShown = tick;
    });
  });
};

const keepWrappedTextBreakable = (root: HTMLElement, win: Window) => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    const value = textNode.textContent;
    const parent = textNode.parentElement;
    if (value?.includes(' ') && parent) {
      const inSvg = parent.namespaceURI === SVG_NS;
      const whiteSpace = win.getComputedStyle(parent).whiteSpace;
      const nowrap = whiteSpace === 'nowrap' || whiteSpace === 'pre';
      if (inSvg || nowrap) textNode.textContent = value.replace(/ /g, '\u00A0');
    }
    textNode = walker.nextNode();
  }
};

const keepTableRowsWrap = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLElement>(
      '.ant-table-wrapper, .ant-table, .ant-table-container, .ant-table-content, .ant-table-body',
    )
    .forEach((element) => {
      element.style.setProperty('overflow', 'visible', 'important');
      element.style.setProperty('width', '100%', 'important');
      element.style.setProperty('max-width', 'none', 'important');
    });

  root.querySelectorAll<HTMLElement>('th, td, .ant-table-cell').forEach((cell) => {
    cell.style.setProperty('min-width', '0', 'important');
    cell.style.setProperty('white-space', 'normal', 'important');
    cell.style.setProperty('overflow', 'visible', 'important');
    cell.style.setProperty('text-overflow', 'clip', 'important');
    cell.style.setProperty('height', 'auto', 'important');
    cell.style.setProperty('max-height', 'none', 'important');
  });
};

export const preparePdfClone = (doc: Document, root: HTMLElement, width: number, height: number) => {
  root.setAttribute('data-pdf-root', 'true');
  root.setAttribute('data-pdf-exporting', 'true');

  doc.querySelectorAll('.no-print').forEach((element) => {
    (element as HTMLElement).style.setProperty('display', 'none', 'important');
  });

  const style = doc.createElement('style');
  style.textContent = `
    [data-pdf-root], [data-pdf-root] * {
      font-family: Arial, Helvetica, sans-serif !important;
      letter-spacing: normal !important;
      word-spacing: normal !important;
      box-sizing: border-box !important;
    }

    [data-pdf-root] svg text {
      font-family: Arial, Helvetica, sans-serif !important;
    }

    [data-pdf-root] .recharts-tooltip-wrapper {
      display: none !important;
    }
  `;
  doc.head.appendChild(style);

  lockBox(root, width, height);
  forceChartStack(root);
  keepTableRowsWrap(root);
  fitChartSvgs(root);
  flattenSvgText(root);

  const win = doc.defaultView;
  if (!win) return;
  keepWrappedTextBreakable(root, win);
  cullOverlappingXTicks(root);
};
